


// import { useEffect, useState } from "react";
// import axiosClient from "../api/axiosClient";
// import { useNavigate, useParams } from "react-router-dom";

// export default function NoteDetails() {
//   const { id } = useParams();
//   const [note, setNote] = useState(null);
//   const navigate = useNavigate();

//   useEffect(() => {
//     axiosClient.get(`/notes/${id}`).then((res) => setNote(res.data));
//   }, [id]);

//   if (!note) return <p>Loading...</p>;

//   const deleteNote = async () => {
//     await axiosClient.delete(`/notes/${id}`);
//     navigate("/notes");
//   };

//   return (
//     <div style={{ padding: "20px" }}>
//       <img src={note.image_url} width="300" />
//       <h1>{note.title}</h1>
//       <p>{note.description}</p> {/* FIXED */}

//       <button onClick={() => navigate(`/edit-note/${id}`)}>Edit</button>
//       <button onClick={deleteNote} style={{ background: "red" }}>
//         Delete
//       </button>
//     </div>
//   );
// }



import { useEffect, useState } from "react";
import axiosClient from "../api/axiosClient";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Card,
  CardMedia
} from "@mui/material";
import {
  Edit,
  Delete,
  ArrowBack,
  Link as LinkIcon,
  CalendarToday
} from "@mui/icons-material";

export default function NoteDetails() {
  const { id } = useParams();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchNote = async () => {
      try {
        setLoading(true);
        const res = await axiosClient.get(`api/notes/${id}`);
        setNote(res.data);
      } catch (err) {
        setError("Failed to load note. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchNote();
  }, [id]);

  const deleteNote = async () => {
    if (!window.confirm("Are you sure you want to delete this note? This action cannot be undone.")) {
      return;
    }

    setDeleteLoading(true);
    try {
      await axiosClient.delete(`api/notes/${id}`);
      navigate("/notes");
    } catch (err) {
      setError("Failed to delete note. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <CircularProgress sx={{ color: "#3E1E68" }} />
        <Typography variant="h6" sx={{ mt: 2, color: "#3E1E68" }}>
          Loading note details...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          sx={{ borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate("/notes")}>
              Back to Notes
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (!note) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Note not found.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate("/notes")}
        sx={{
          color: "#3E1E68",
          mb: 3,
          "&:hover": {
            backgroundColor: "rgba(62, 30, 104, 0.1)"
          }
        }}
      >
        Back to Notes
      </Button>

      <Paper
        elevation={3}
        sx={{
          p: 4,
          // background: "linear-gradient(135deg, #3E1E68 0%, #5D2F77 100%)",
          background:"#2a1c32ff",
          color: "white",
          borderRadius: 3
        }}
      >
        {/* Header Section */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Chip
              icon={<CalendarToday />}
              label="Note Details"
              variant="outlined"
              sx={{ color: "white", borderColor: "white", mb: 2 }}
            />
            <Typography variant="h3" component="h1" fontWeight="bold">
              {note.title}
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 1, ml: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => navigate(`/edit-note/${id}`)}
              sx={{
                color: "white",
                borderColor: "white",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.1)"
                }
              }}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              startIcon={deleteLoading ? <CircularProgress size={16} /> : <Delete />}
              onClick={deleteNote}
              disabled={deleteLoading}
              sx={{
                backgroundColor: "#ff4444",
                color: "white",
                "&:hover": {
                  backgroundColor: "#cc0000"
                },
                "&:disabled": {
                  backgroundColor: "rgba(255,68,68,0.5)"
                }
              }}
            >
              {deleteLoading ? "Deleting..." : "Delete"}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ backgroundColor: "rgba(255,255,255,0.3)", my: 3 }} />

        {/* Content Section */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4 }}>
          {/* Image Section */}
          {note.image_url && (
            <Box sx={{ flex: { md: 1 } }}>
              <Card sx={{ borderRadius: 2, overflow: "hidden" }}>
                <CardMedia
                  component="img"
                  image={note.image_url}
                  alt={note.title}
                  sx={{
                    width: "100%",
                    maxHeight: 400,
                    objectFit: "cover"
                  }}
                />
              </Card>
            </Box>
          )}

          {/* Text Content */}
          <Box sx={{ flex: { md: 2 } }}>
            {/* Description */}
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Description
            </Typography>
            <Paper
              sx={{
                p: 3,
                backgroundColor: "rgba(255,255,255,0.1)",
                borderRadius: 2,
                mb: 4
              }}
            >
              <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
                {note.description}
              </Typography>
            </Paper>

            {/* Reference Link */}
            {note.reference_link && (
              <>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  Reference
                </Typography>
                <Button
                  component="a"
                  href={note.reference_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<LinkIcon />}
                  variant="outlined"
                  sx={{
                    color: "white",
                    borderColor: "white",
                    "&:hover": {
                      borderColor: "white",
                      backgroundColor: "rgba(255,255,255,0.1)"
                    }
                  }}
                >
                  Open Reference Link
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Metadata */}
        <Box sx={{ mt: 4, pt: 2, borderTop: "1px solid rgba(255,255,255,0.2)" }}>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            Note ID: {note.id} • Created: {new Date(note.created_at).toLocaleDateString()}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}